/**
 * Internationalization support for validation messages
 */

import { createGlobalState } from '@vueuse/core'
import { ref } from 'vue'

// Validation configuration constants (copied to avoid circular imports)
const ValidationConfig = {
  TFA_TOKEN_LENGTH: 6,
  CONFIRMATION_CODE_LENGTH: 6,
  PASSWORD_STRONG_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 256,
  USERNAME_MIN_LENGTH: 4,
  PASSWORD_MIN_LENGTH: 6,
  AMOUNT_MIN: 0.001,
  AMOUNT_MAX: 0.5
} as const

// Global i18n function reference for validation messages
const useGlobalI18n = createGlobalState(() => {
  const i18nFunction = ref<((key: string, options?: any) => string) | null>(null)
  return { i18nFunction }
})

type ValidationI18nListener = () => void
const listeners = new Set<ValidationI18nListener>()

// Reverse lookup cache to map rendered messages back to validation keys
const reverseMessageMap = new Map<string, Set<string>>()

function registerMessage(key: string, message: string) {
  if (!message || typeof message !== 'string') return

  const existing = reverseMessageMap.get(message)
  if (existing) {
    existing.add(key)
    return
  }

  reverseMessageMap.set(message, new Set([key]))
}

/**
 * Sets up i18n function for validation messages
 */
export function setupValidationI18n(t: (key: string, options?: any) => string) {
  const { i18nFunction } = useGlobalI18n()
  i18nFunction.value = t

  listeners.forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error('Error running validation i18n listener', error)
    }
  })
}

export function onValidationI18nChange(listener: ValidationI18nListener) {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/**
 * Mapping of ValidationMessages keys to i18n keys with fallbacks
 */
const messageKeyMap: Record<string, { key: string; fallback: string; options?: any }> = {
  // Generic field messages
  FIELD_REQUIRED: { key: 'validation.required', fallback: 'This field is required' },

  // Email messages
  EMAIL_REQUIRED: { key: 'validation.emailRequired', fallback: 'Email is required' },
  EMAIL_INVALID: { key: 'validation.emailInvalid', fallback: 'Invalid email address' },

  // Username messages
  USERNAME_REQUIRED: { key: 'validation.usernameRequired', fallback: 'Username is required' },
  USERNAME_MIN_LENGTH: { key: 'validation.usernameMinLength', fallback: `Username must be at least ${ValidationConfig.USERNAME_MIN_LENGTH} characters` },
  USERNAME_INVALID_CHARS: { key: 'validation.invalidUsername', fallback: 'Username can only contain letters, numbers, dots, and underscores' },
  USERNAME_EXISTS: { key: 'validation.usernameExists', fallback: 'Username already exists' },

  // Password messages
  PASSWORD_REQUIRED: { key: 'validation.passwordRequired', fallback: 'Password is required' },
  PASSWORD_MIN_LENGTH: { key: 'validation.passwordMinLength', fallback: `Password must be at least ${ValidationConfig.PASSWORD_MIN_LENGTH} characters` },
  PASSWORD_STRONG_MIN_LENGTH: { key: 'validation.passwordTooShort', fallback: `Password must be at least ${ValidationConfig.PASSWORD_STRONG_MIN_LENGTH} characters` },
  PASSWORD_NEED_UPPERCASE: { key: 'forms.passwordRequirements.uppercase', fallback: 'Password must contain at least one uppercase letter' },
  PASSWORD_NEED_LOWERCASE: { key: 'forms.passwordRequirements.lowercase', fallback: 'Password must contain at least one lowercase letter' },
  PASSWORD_NEED_NUMBER: { key: 'forms.passwordRequirements.numbers', fallback: 'Password must contain at least one number' },
  PASSWORD_NEED_SPECIAL: { key: 'forms.passwordRequirements.specialChars', fallback: `Password must contain at least one special character (!"#$%&'()*+,-./:;<=>?@[\\]^_\`{|}~)` },
  PASSWORD_STRONG: { key: 'validation.passwordStrong', fallback: 'Password must contain uppercase, lowercase, number and special character' },
  PASSWORD_CONFIRM_REQUIRED: { key: 'validation.passwordConfirmRequired', fallback: 'Please confirm your password' },
  PASSWORD_CONFIRM_NEW_REQUIRED: { key: 'validation.passwordConfirmRequired', fallback: 'Please confirm your new password' },
  PASSWORD_MISMATCH: { key: 'validation.passwordMismatch', fallback: 'Passwords do not match' },
  PASSWORD_SAME_AS_CURRENT: { key: 'validation.passwordSameAsCurrent', fallback: 'New password must be different from current password' },
  PASSWORD_CURRENT_REQUIRED: { key: 'validation.passwordCurrentRequired', fallback: 'Current password is required' },
  PASSWORD_CURRENT_INCORRECT: { key: 'validation.passwordCurrentIncorrect', fallback: 'Current password is incorrect' },
  PASSWORD_GUEST_REQUIRED: { key: 'validation.passwordGuestRequired', fallback: 'Password is required (can be any value like "x")' },
  PASSWORD_LENGTH_RANGE: { key: 'validation.passwordLengthRange', fallback: `Password must be between ${ValidationConfig.PASSWORD_STRONG_MIN_LENGTH} and ${ValidationConfig.PASSWORD_MAX_LENGTH} characters` },

  // Wallet messages
  WALLET_REQUIRED: { key: 'validation.walletAddressRequired', fallback: 'Bitcoin wallet address is required' },
  WALLET_INVALID: { key: 'validation.walletAddressInvalid', fallback: 'Invalid Bitcoin address format. Supported formats: P2PKH, P2SH, Bech32, Bech32m' },
  WALLET_ADDRESS_INVALID: { key: 'validation.walletAddressInvalid', fallback: 'Invalid wallet address' },

  // Dynamic wallet messages (currency-specific)
  WALLET_BTC_REQUIRED: { key: 'validation.walletBtcRequired', fallback: 'Bitcoin wallet address is required' },
  WALLET_BTC_INVALID: { key: 'validation.walletBtcInvalid', fallback: 'Invalid Bitcoin address format. Supported formats: P2PKH, P2SH, Bech32, Bech32m' },
  WALLET_LTC_REQUIRED: { key: 'validation.walletLtcRequired', fallback: 'Litecoin wallet address is required' },
  WALLET_LTC_INVALID: { key: 'validation.walletLtcInvalid', fallback: 'Invalid Litecoin address format. Supported formats: P2PKH (L...), P2SH (M.../3...), Bech32 (ltc1...), Bech32m (ltc1p...)' },

  // Amount messages
  AMOUNT_REQUIRED: { key: 'validation.minimumAmountRequired', fallback: 'Minimum amount is required' },
  AMOUNT_POSITIVE: { key: 'validation.amountPositive', fallback: 'Amount must be greater than 0' },
  AMOUNT_RANGE: { key: 'validation.amountRange', fallback: `Minimum amount must be between ${ValidationConfig.AMOUNT_MIN} and ${ValidationConfig.AMOUNT_MAX} BTC` },
  AMOUNT_INVALID: { key: 'validation.minimumAmountInvalid', fallback: 'Invalid minimum amount' },

  // 2FA messages
  TFA_REQUIRED: { key: 'validation.twoFactorRequired', fallback: '2FA code is required' },
  TFA_FORMAT: { key: 'validation.invalid2FACode', fallback: `2FA code must be ${ValidationConfig.TFA_TOKEN_LENGTH} digits` },
  TFA_DIGITS_ONLY: { key: 'validation.tfaDigitsOnly', fallback: '2FA code must contain only digits' },
  TFA_INVALID: { key: 'validation.twoFactorInvalid', fallback: 'Invalid 2FA code' },
  TFA_ENTER_VALID: { key: 'validation.tfaEnterValid', fallback: 'Please enter a valid 2FA code' },
  TFA_AUTHENTICATION_INVALID: { key: 'validation.tfaAuthenticationInvalid', fallback: 'Invalid 2FA authentication code' },
  TFA_MISSING: { key: 'validation.tfaMissing', fallback: 'Please enter your 2FA code' },
  TFA_ALREADY_ENABLED: { key: 'validation.tfaAlreadyEnabled', fallback: '2FA is already enabled' },
  TFA_NOT_ENABLED: { key: 'validation.tfaNotEnabled', fallback: '2FA is not enabled' },
  TFA_SETUP_REQUIRED: { key: 'validation.tfaSetupRequired', fallback: 'To link a wallet, you need to set up 2FA' },

  // Confirmation code messages
  CONFIRMATION_CODE_REQUIRED: { key: 'validation.confirmationCodeRequired', fallback: 'Confirmation code is required' },
  CONFIRMATION_CODE_FORMAT: { key: 'validation.confirmationCodeFormat', fallback: `Confirmation code must be exactly ${ValidationConfig.CONFIRMATION_CODE_LENGTH} digits` },
  CONFIRMATION_CODE_DIGITS_ONLY: { key: 'validation.confirmationCodeDigitsOnly', fallback: 'Confirmation code must contain only digits' },
  CONFIRMATION_CODE_INVALID: { key: 'validation.confirmationCodeInvalid', fallback: 'Invalid confirmation code' },
  CONFIRMATION_CODE_EXPIRED: { key: 'validation.confirmationCodeExpired', fallback: 'Confirmation code has expired' },

  // Token messages
  TOKEN_REQUIRED: { key: 'validation.tokenRequired', fallback: 'Reset token is required' },
  TOKEN_RESET_EXPIRED: { key: 'validation.tokenResetExpired', fallback: 'Password reset link has expired' },
  TOKEN_RESET_INVALID: { key: 'validation.tokenResetInvalid', fallback: 'Invalid password reset link' },
  TOKEN_CHANGE_WALLET_EXPIRED: { key: 'validation.tokenChangeWalletExpired', fallback: 'Wallet change token has expired' },
  TOKEN_CHANGE_WALLET_INVALID: { key: 'validation.tokenChangeWalletInvalid', fallback: 'Invalid wallet change token' },
  TOKEN_CONFIRMATION_EXPIRED: { key: 'validation.tokenConfirmationExpired', fallback: 'Confirmation token expired' },
  TOKEN_CONFIRMATION_INVALID: { key: 'validation.tokenConfirmationInvalid', fallback: 'Invalid confirmation token' },

  // User messages
  USER_ID_REQUIRED: { key: 'validation.userIdRequired', fallback: 'User ID is required' },
  USER_NOT_FOUND: { key: 'validation.userNotFound', fallback: 'User not found' },
  USER_NOT_EXISTS: { key: 'validation.userNotExists', fallback: 'User does not exist' },
  USER_EMAIL_EXISTS: { key: 'validation.userEmailExists', fallback: 'Email already registered' },

  // Address input messages
  ADDRESS_REQUIRED: { key: 'validation.addressRequired', fallback: 'Bitcoin address is required' },
  ADDRESS_ENTER: { key: 'validation.addressEnter', fallback: 'Please enter a wallet address' },
  ADDRESS_ENTER_MINIMUM: { key: 'validation.addressEnterMinimum', fallback: 'Please enter minimum amount' },

  // Auth messages
  AUTH_LOGIN_REQUIRED: { key: 'validation.authLoginRequired', fallback: 'Please log in to continue' },
  AUTH_INVALID_CREDENTIALS: { key: 'validation.authInvalidCredentials', fallback: 'Invalid email or password' },
  AUTH_ACCOUNT_LOCKED: { key: 'validation.authAccountLocked', fallback: 'Account is locked' },
  CAPTCHA_REQUIRED: { key: 'auth.captchaRequired', fallback: 'Please complete the CAPTCHA verification' },
  CAPTCHA_INVALID: { key: 'auth.captchaInvalid', fallback: 'Invalid CAPTCHA. Please try again.' },

  // Success messages
  SUCCESS_PASSWORD_CHANGED: { key: 'validation.successPasswordChanged', fallback: 'Password changed successfully!' },
  SUCCESS_PASSWORD_CHANGED_RELOGIN: { key: 'validation.successPasswordChangedRelogin', fallback: 'Password changed. For security reasons, please log in again' },
  SUCCESS_EMAIL_CHANGED: { key: 'validation.successEmailChanged', fallback: 'Email changed successfully!' },
  SUCCESS_WALLET_SAVED: { key: 'validation.successWalletSaved', fallback: 'Wallet settings saved successfully!' },
  SUCCESS_WALLET_SAVED_CONFIRM: { key: 'validation.successWalletSavedConfirm', fallback: 'Wallet settings saved! Please check your email for confirmation.' },
  SUCCESS_OPERATION_COMPLETED: { key: 'validation.successOperationCompleted', fallback: 'Operation completed successfully' },
  SUCCESS_PAYMENT_THRESHOLD_UPDATED: { key: 'validation.successPaymentThresholdUpdated', fallback: 'Payment threshold updated successfully' },
  SUCCESS_WALLET_ADDRESS_UPDATED: { key: 'validation.successWalletAddressUpdated', fallback: 'Wallet address updated successfully' },
  SUCCESS_CONFIRMATION_EMAIL_SENT: { key: 'validation.successConfirmationEmailSent', fallback: 'Confirmation email sent' },
  SUCCESS_REGISTRATION: { key: 'validation.successRegistration', fallback: 'Registration successful! You can now log in.' },
  SUCCESS_PASSWORD_RESET_SENT: { key: 'validation.successPasswordResetSent', fallback: 'Password reset link sent to your email' },
  SUCCESS_PASSWORD_UPDATED: { key: 'validation.successPasswordUpdated', fallback: 'Password updated successfully! Please log in with your new password.' },
  SUCCESS_PASSWORD_RESET: { key: 'validation.successPasswordReset', fallback: 'Password reset successfully!' },
  SUCCESS_VERIFICATION_CODE_RESENT: { key: 'validation.successVerificationCodeResent', fallback: 'Verification code has been resent to your email' },
  SUCCESS_2FA_ENABLED: { key: 'validation.success2faEnabled', fallback: 'Two-factor authentication has been successfully enabled!' },
  SUCCESS_2FA_DISABLED: { key: 'validation.success2faDisabled', fallback: 'Two-factor authentication has been successfully disabled!' },
  SUCCESS_LOGOUT: { key: 'validation.successLogout', fallback: 'You have successfully logged out' },
  SUCCESS_LOGOUT_LOCAL: { key: 'validation.successLogoutLocal', fallback: 'You have logged out (locally)' },

  // Error messages
  ERROR_UNKNOWN: { key: 'messages.error', fallback: 'Unknown error' },
  ERROR_OCCURRED: { key: 'validation.errorOccurred', fallback: 'An error occurred while processing the request' },
  ERROR_NETWORK: { key: 'validation.errorNetwork', fallback: 'Network error' },
  ERROR_SERVER: { key: 'validation.errorServer', fallback: 'Server error. Please try again later' },
  ERROR_RATE_LIMITED: { key: 'validation.errorRateLimited', fallback: 'Too many requests. Please try again later' },
  ERROR_WALLET_SAVE_FAILED: { key: 'validation.errorWalletSaveFailed', fallback: 'Wallet settings save failed' },
  ERROR_2FA_VALIDATION_FAILED: { key: 'validation.error2faValidationFailed', fallback: '2FA validation failed' },
  ERROR_PASSWORD_CHANGE_FAILED: { key: 'validation.errorPasswordChangeFailed', fallback: 'Failed to change password. Please check your current password and try again.' },
  ERROR_INVALID_PAYMENT_THRESHOLD: { key: 'validation.errorInvalidPaymentThreshold', fallback: 'Invalid payment threshold' },
  ERROR_INVALID_USER_ADDRESS: { key: 'validation.errorInvalidUserAddress', fallback: 'Invalid user address' },
  ERROR_TIMEOUT: { key: 'validation.errorTimeout', fallback: 'Request timeout' },
  ERROR_INVALID_CREDENTIALS: { key: 'validation.errorInvalidCredentials', fallback: 'Invalid credentials' },
  ERROR_NO_DATA_TO_EXPORT: { key: 'messages.noDataToExport', fallback: 'No data to export' },
  ERROR_EXPORT_CSV_FAILED: { key: 'messages.exportCsvFailed', fallback: 'Failed to export CSV file' },
  ERROR_EXPORT_EXCEL_FAILED: { key: 'messages.exportExcelFailed', fallback: 'Failed to export Excel file' },
  ERROR_COPY_FAILED: { key: 'validation.errorCopyFailed', fallback: 'Failed to copy code' },
  ERROR_QR_CODE_LOAD_FAILED: { key: 'modals.failedToLoadQR', fallback: 'Failed to load QR code. Please try refreshing or use manual entry.' },
  ERROR_SESSION_EXPIRED: { key: 'validation.errorSessionExpired', fallback: 'Session expired. Please refresh the page or log in again.' },
  ERROR_LOGOUT: { key: 'validation.errorLogout', fallback: 'An error occurred during logout, but you were disconnected locally' },
  ERROR_LOADING_ACCOUNT_OVERVIEW: { key: 'validation.errorLoadingAccountOverview', fallback: 'Error loading account overview' },
  ERROR_LOADING_WORKERS: { key: 'validation.errorLoadingWorkers', fallback: 'Error loading workers' },
  ERROR_LOADING_EARNINGS: { key: 'validation.errorLoadingEarnings', fallback: 'Error loading earnings' },
  ERROR_LOADING_PAYMENT_HISTORY: { key: 'validation.errorLoadingPaymentHistory', fallback: 'Error loading payment history' },
  ERROR_DATE_PARSING_FAILED: { key: 'validation.errorDateParsingFailed', fallback: 'Date parsing failed' },
  ERROR_SEARCH_FAILED: { key: 'validation.errorSearchFailed', fallback: 'Search failed' },
  ERROR_ACCESS_DENIED: { 
    key: 'validation.errorAccessDenied', 
    fallback: 'This user has restricted access to their statistics' 
  },

  // Form-specific error messages
  ERROR_SEND_CONFIRMATION_CODE_FAILED: { key: 'validation.errorSendConfirmationCodeFailed', fallback: 'Failed to send confirmation code. Please try again.' },
  ERROR_REGISTRATION_FAILED: { key: 'validation.errorRegistrationFailed', fallback: 'Failed to create account. Please try again.' },
  ERROR_LOGIN_FAILED: { key: 'validation.errorLoginFailed', fallback: 'Login failed. Please try again.' },
  ERROR_RESET_PASSWORD_SEND_FAILED: { key: 'validation.errorResetPasswordSendFailed', fallback: 'Failed to send reset link. Please try again.' },
  ERROR_EMAIL_CHANGE_FAILED: { key: 'validation.errorEmailChangeFailed', fallback: 'Failed to change email. Please try again.' },
  ERROR_RESEND_VERIFICATION_FAILED: { key: 'validation.errorResendVerificationFailed', fallback: 'Failed to resend verification code. Please try again.' },
  ERROR_INVALID_RESET_LINK: { key: 'validation.errorInvalidResetLink', fallback: 'Invalid reset link. Please request a new password reset.' },
  ERROR_EMAIL_MISSING: { key: 'validation.errorEmailMissing', fallback: 'Email is missing. Please go back and try again.' },
  ERROR_DIFFERENT_EMAIL_REQUIRED: { key: 'validation.errorDifferentEmailRequired', fallback: 'Please enter a different email address' },
  ERROR_INVALID_CONFIRMATION_LINK: { key: 'validation.errorInvalidConfirmationLink', fallback: 'Invalid confirmation link. Please request a new password reset.' },
  ERROR_RESET_LINK_EXPIRED_OR_INVALID: { key: 'validation.errorResetLinkExpiredOrInvalid', fallback: 'Reset link has expired or is invalid. Please request a new one.' },
  ERROR_EMAIL_CONFIRMATION_RATE_LIMIT: { key: 'validation.errorEmailConfirmationRateLimit', fallback: 'Too many email confirmation attempts. Please try again later' }
}

function resolveMessage(prop: string): string {
  const { i18nFunction } = useGlobalI18n()

  if (!(prop in messageKeyMap)) {
    return prop
  }

  const messageConfig = messageKeyMap[prop]

  if (i18nFunction.value) {
    try {
      return i18nFunction.value(messageConfig.key, messageConfig.options)
    } catch (error) {
      console.warn(`Translation key '${messageConfig.key}' not found, using fallback`)
      return messageConfig.fallback
    }
  }

  return messageConfig.fallback
}

/**
 * Creates internationalized ValidationMessages proxy
 */
export function createValidationMessages() {
  return new Proxy({} as Record<string, string>, {
    get(target, prop: string) {
      if (typeof prop !== 'string') {
        return (target as any)[prop]
      }

      const message = resolveMessage(prop)
      registerMessage(prop, message)
      return message
    }
  })
}

/**
 * Translates an existing validation message into the current locale
 */
export function translateValidationMessage(message: string): string {
  if (!message || typeof message !== 'string') {
    return message
  }

  const keys = reverseMessageMap.get(message)

  if (!keys || keys.size === 0) {
    return message
  }

  for (const key of keys) {
    const translatedMessage = resolveMessage(key)
    registerMessage(key, translatedMessage)

    if (translatedMessage !== message) {
      return translatedMessage
    }
  }

  return message
}
